/**
 * SEO redirect map.
 *
 * Search Console reported "Page with redirect" and "Alternate page with proper
 * canonical" for a long tail of legacy URLs (the old Eidrolon Central slugs,
 * renamed guides, retired variant pages). Those must resolve to exactly one
 * live URL with a single 301-style hop — never a chain, never the homepage for
 * unrelated paths (that is what produced the soft-404 reports).
 *
 * Everything not listed here still falls through to a real 404 in
 * `src/routes/$.tsx`.
 */
export interface RedirectRule {
  /** Legacy path, no trailing slash, no query string. */
  from: string;
  /** Live destination path. */
  to: string;
  /** Why the URL moved — shown in the admin redirect map. */
  reason: string;
}

export const REDIRECTS: RedirectRule[] = [
  // ── Rebrand: Eidrolon Central → Pal Stars ──────────────────────────────
  { from: "/eidrolon-central", to: "/", reason: "Rebrand to Pal Stars" },
  { from: "/eidrolon-central/guides", to: "/guides", reason: "Rebrand to Pal Stars" },
  { from: "/eidrolon-guide", to: "/eidrolon", reason: "Guide folded into the Eidrolon hub" },
  { from: "/eidrolon-starter-guide", to: "/guides/starter", reason: "Renamed to Palworld Starter Guide" },
  { from: "/guides/eidrolon-starter-guide", to: "/guides/starter", reason: "Renamed to Palworld Starter Guide" },

  // ── Retired internal pages ─────────────────────────────────────────────
  { from: "/memes", to: "/", reason: "Never existed; GSC 404 cleanup" },
  { from: "/price-automation", to: "/prices", reason: "Price-sync health moved into the admin console" },

  // ── Renamed tools ──────────────────────────────────────────────────────
  { from: "/dps", to: "/damage", reason: "Tool renamed to the damage calculator" },
  { from: "/dps-calculator", to: "/damage", reason: "Tool renamed to the damage calculator" },
  { from: "/damage-calculator", to: "/damage", reason: "Canonical short path" },
  { from: "/element-chart", to: "/elements", reason: "Canonical short path" },
  { from: "/type-chart", to: "/elements", reason: "Canonical short path" },
  { from: "/breeding-calculator", to: "/breeding", reason: "Canonical short path" },
  { from: "/breeding-combos", to: "/breeding", reason: "Canonical short path" },
  { from: "/catch-rates", to: "/capture-rates", reason: "Canonical short path" },
  { from: "/flying-mounts", to: "/mounts", reason: "Merged into the mount speed chart" },
  { from: "/mount-speeds", to: "/mounts", reason: "Canonical short path" },
  { from: "/tech-tree", to: "/technology-tree", reason: "Canonical short path" },
  { from: "/card-prices", to: "/prices", reason: "Canonical short path" },
  { from: "/tcg-prices", to: "/prices", reason: "Canonical short path" },
  { from: "/slots", to: "/palsino", reason: "Renamed to Palsino" },
  { from: "/casino", to: "/palsino", reason: "Renamed to Palsino" },
  { from: "/packs", to: "/booster-pack", reason: "Canonical pack landing page" },
  { from: "/pack", to: "/booster-pack", reason: "Canonical pack landing page" },
  { from: "/open-packs", to: "/pack-opening", reason: "Canonical pack game path" },

  // ── Farming / gold consolidation ───────────────────────────────────────
  { from: "/gold-farm", to: "/afk-gold", reason: "Consolidated into the AFK gold landing page" },
  { from: "/afk-gold-farm", to: "/afk-gold", reason: "Consolidated into the AFK gold landing page" },
  { from: "/guides/afk-gold", to: "/afk-gold", reason: "Promoted to a top-level landing page" },
  { from: "/money-farm", to: "/afk-gold", reason: "Consolidated into the AFK gold landing page" },
  { from: "/farming", to: "/guides/farming", reason: "Guide lives under /guides" },

  // ── Directory-style legacy paths ───────────────────────────────────────
  { from: "/pal", to: "/pals", reason: "Index path" },
  { from: "/pals-list", to: "/pals", reason: "Index path" },
  { from: "/database", to: "/pals", reason: "Database index is the Pal directory" },
  { from: "/wiki", to: "/", reason: "Wiki home is the site home" },
  { from: "/guide", to: "/guides", reason: "Index path" },
  { from: "/articles", to: "/guides", reason: "Index path" },
  { from: "/tcg", to: "/cards", reason: "Card database is the TCG hub" },
  { from: "/tcg-cards", to: "/cards", reason: "Canonical card database path" },
  { from: "/deck", to: "/deck-builder", reason: "Canonical deck builder path" },
  { from: "/team", to: "/team-builder", reason: "Canonical team builder path" },
  { from: "/boss", to: "/bosses", reason: "Index path" },
  { from: "/egg", to: "/eggs", reason: "Index path" },
  { from: "/tiers", to: "/tier-list", reason: "Canonical tier list path" },
  { from: "/meta", to: "/tier-list", reason: "Meta ranking lives on the tier list" },
];

const BY_FROM = new Map(REDIRECTS.map((r) => [r.from, r.to]));

/**
 * Candidate base slugs for a retired fan-variant slug.
 *
 * Search Console still crawls the deleted variant URLs (`necromus-void`,
 * `lamball-solar`, `foxparks-prime`, `neo-rooby`, ...). Each of those has a real
 * base page (`necromus`, `lamball`, `foxparks`, `rooby`), so the variant URL
 * gets one permanent hop there instead of reporting a 404. Slugs with no base
 * match keep answering 404 — that is the correct signal for a page that never
 * had a live equivalent.
 */
export function baseSlugCandidates(slug: string): string[] {
  const parts = slug.toLowerCase().split("-").filter(Boolean);
  if (parts.length < 2) return [];
  const out: string[] = [];
  // Every contiguous sub-run, longest first: "neo-frost-rooby-void" tries
  // "neo-frost-rooby", "frost-rooby-void", "frost-rooby", "rooby", ...
  for (let len = parts.length - 1; len >= 1; len--) {
    for (let start = 0; start + len <= parts.length; start++) {
      out.push(parts.slice(start, start + len).join("-"));
    }
  }
  return out.filter((s) => s && s !== slug);
}

/** First candidate base slug that exists, or undefined. */
export function resolveRetiredSlug(slug: string, exists: (s: string) => boolean): string | undefined {
  return baseSlugCandidates(slug).find(exists);
}

/**
 * Section index for a retired slug with no base match.
 *
 * Google reported 116 "Not found (404)" URLs, all of them deleted fan-variant
 * Pal/guide pages. The user requires zero 404s on those patterns, so anything
 * the base-slug matcher cannot place gets one permanent hop to its section
 * index instead of a 404.
 */
export function retiredSectionIndex(section: "pals" | "guides"): string {
  return section === "pals" ? "/pals" : "/guides";
}

/** Destination for a legacy path, or undefined when the path is genuinely unknown. */
export function redirectFor(pathname: string): string | undefined {
  const clean = (pathname.split("?")[0].replace(/\/+$/, "") || "/").toLowerCase();
  return BY_FROM.get(clean);
}

/** Redirect chains and self-loops — must always be empty (checked in the admin map). */
export function redirectProblems(): string[] {
  const problems: string[] = [];
  for (const r of REDIRECTS) {
    if (r.from === r.to) problems.push(`${r.from} redirects to itself`);
    if (BY_FROM.has(r.to)) problems.push(`${r.from} → ${r.to} is a chain (${r.to} also redirects)`);
  }
  return problems;
}
